import React, { useEffect, useState } from 'react';
import { useEventBasedCurrencyConverter } from '../hooks/useEventBasedCurrencyConverter';
import { logInfo, logError } from '../utils/logging';

const CurrencyContext = React.createContext();

function getCurrency() {
  // https://apps.shopify.com/doubly-currency-converter
  const doublyCurrency =
    window.DoublyGlobalCurrency && window.DoublyGlobalCurrency.currentCurrency;
  if (doublyCurrency && doublyCurrency.length === 3) {
    return doublyCurrency;
  }

  // https://apps.shopify.com/currency-switcher
  const scmCurrency =
    window.SECOMAPP &&
    typeof window.SECOMAPP.cookie === 'function' &&
    window.SECOMAPP.cookie('scm_currency_2');
  if (scmCurrency && scmCurrency.length === 3) {
    return scmCurrency;
  }

  // https://apps.shopify.com/multi-currency
  const boldCurrency =
    window.BOLDCURRENCY && window.BOLDCURRENCY.currentCurrency;
  if (boldCurrency && boldCurrency.length === 3) {
    return boldCurrency;
  }

  // https://apps.shopify.com/coin
  const coinCurrency =
    window.Shoppad &&
    window.Shoppad.apps &&
    window.Shoppad.apps.coin &&
    typeof window.Shoppad.apps.coin.getLocalCurrency === 'function' &&
    window.Shoppad.apps.coin.getLocalCurrency();
  if (coinCurrency && coinCurrency.length === 3) {
    return coinCurrency;
  }

  // https://withreach.com/
  const reachCurrency = localStorage.getItem('GIP_USER_CURRENCY');
  if (reachCurrency && reachCurrency.length === 3) {
    return reachCurrency;
  }

  // https://apps.shopify.com/vitals
  const vitalsCurrency = localStorage.getItem('local_currency');
  if (vitalsCurrency && vitalsCurrency.length === 3) {
    return vitalsCurrency;
  }

  // https://apps.shopify.com/currency-converter-master
  try {
    const autoketingCurrency = JSON.parse(
      localStorage.getItem('autoketing-currency-location-customer-v1'),
    );
    if (autoketingCurrency && autoketingCurrency.length === 3) {
      return autoketingCurrency;
    }
  } catch (e) {
    console.error('JSON.parse error for: autoketingCurrency');
  }

  const currencyJs = window.Currency && window.Currency.currentCurrency;
  if (currencyJs && currencyJs.length === 3) {
    return currencyJs;
  }

  // https://apps.shopify.com/auto-multi-currency-converter
  const currencyBearJs = window.conversionBearAutoCurrencyConverter?.currency;
  if (currencyBearJs) {
    return currencyBearJs;
  }

  return window.Shopify.currency.active;
}

function affixScriptToHead(url) {
  return new Promise((resolve, reject) => {
    var newScript = document.createElement('script');
    newScript.onerror = (oError) => {
      reject(
        new URIError(
          'The script ' + oError.target.src + " didn't load correctly.",
        ),
      );
    };
    newScript.onload = resolve;
    document.head.appendChild(newScript);
    newScript.src = url;
  });
}

export const CurrencyContextProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState(getCurrency());
  const storeActiveCurrency = window.Shopify.currency.active;

  useEventBasedCurrencyConverter((currency) => setSelectedCurrency(currency));

  useEffect(() => {
    async function fetchAndDoStuffWithData() {
      try {
        if (storeActiveCurrency !== selectedCurrency && !window.Currency) {
          logInfo(
            `Found currency conversion app. Selected currency: ${selectedCurrency}`,
          );
          await affixScriptToHead(
            'https://cdn.shopify.com/s/javascripts/currencies.js',
          );
        }
      } catch (error) {
        logError(error);
      }
    }
    fetchAndDoStuffWithData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function convertPrice(price) {
    if (
      storeActiveCurrency !== selectedCurrency &&
      window.Currency &&
      window.Currency.convert
    ) {
      return window.Currency.convert(
        Number(price),
        storeActiveCurrency,
        selectedCurrency,
      );
    }

    return price;
  }

  return (
    <CurrencyContext.Provider
      value={{ currency: selectedCurrency, convertPrice }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
